/**
 * Sprint T-16.1 · Automation Center · HistoryTab.
 *
 * Búsqueda + paginación completa de ExecutionRecord.
 * Filtro por fechas dejado como hueco visual (§17.5 · no backend support).
 */
import React, { useMemo, useState } from "react";
import { Filter, RefreshCw, Info } from "lucide-react";
import { WidgetCard, EmptyState, LoadingState, CitationBadge } from "../advisor";
import { useAutomationJobs, useExecutions } from "../../hooks/useAutomation";
import ExecutionsTable from "./shared/ExecutionsTable";
import { ExecutionDetailPanel } from "./shared/DetailPanels";

const PAGE_SIZE = 50;

const STATUSES = ["success", "failed", "dead", "pending", "retry", "running", "paused"];


export default function HistoryTab() {
  const [filters, setFilters] = useState({ job_name: "", status: "" });
  const [applied, setApplied] = useState(filters);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);

  const jobsQ = useAutomationJobs();
  const jobOptions = useMemo(
    () => (jobsQ.data?.items || []).map((j) => j.name).sort(),
    [jobsQ.data]
  );

  const query = useMemo(
    () => ({
      job_name: applied.job_name || undefined,
      status: applied.status || undefined,
      limit: PAGE_SIZE,
      skip: page * PAGE_SIZE,
    }),
    [applied, page]
  );
  const q = useExecutions(query);
  const items = q.data?.items || [];
  const total = q.data?.total ?? 0;

  const apply = (e) => {
    e?.preventDefault?.();
    setPage(0);
    setApplied(filters);
  };

  const clear = () => {
    const empty = { job_name: "", status: "" };
    setFilters(empty);
    setApplied(empty);
    setPage(0);
  };

  return (
    <section className="space-y-4" data-testid="automation-history-tab">
      <WidgetCard
        title="Filtros"
        description="job_name + status · el filtro por fecha llegará cuando el backend lo soporte (T-16.2)."
        citations={["/api/automation/executions"]}
        testId="widget-history-filters"
      >
        <form
          onSubmit={apply}
          className="grid grid-cols-1 gap-3 md:grid-cols-5"
          data-testid="history-filters-form"
        >
          <div className="md:col-span-2">
            <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">job_name</label>
            <select
              data-testid="history-filter-job"
              value={filters.job_name}
              onChange={(e) => setFilters((f) => ({ ...f, job_name: e.target.value }))}
              className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 text-xs"
            >
              <option value="">Todos</option>
              {jobOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">status</label>
            <select
              data-testid="history-filter-status"
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 text-xs"
            >
              <option value="">Todos</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2 flex items-end gap-2">
            <button
              type="submit"
              data-testid="history-apply-btn"
              className="inline-flex items-center gap-1 rounded bg-emerald-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-600"
            >
              <Filter className="h-3 w-3" /> Aplicar
            </button>
            <button
              type="button"
              data-testid="history-clear-btn"
              onClick={clear}
              className="rounded border border-[var(--ag-border)] px-3 py-1.5 text-xs hover:bg-[var(--ag-surface-2)]"
            >
              Limpiar
            </button>
            <button
              type="button"
              data-testid="history-refresh-btn"
              onClick={() => q.refetch()}
              className="rounded border border-[var(--ag-border)] px-3 py-1.5 text-xs hover:bg-[var(--ag-surface-2)]"
              aria-label="Refrescar"
            >
              <RefreshCw className={`h-3 w-3 ${q.isFetching ? "animate-spin" : ""}`} />
            </button>
          </div>
        </form>

        <div
          data-testid="history-date-placeholder"
          className="mt-3 rounded border border-dashed border-[var(--ag-border)] bg-[var(--ag-surface-2)]/40 p-2 text-[10px] text-[var(--ag-text-dim)]"
        >
          <Info className="mr-1 inline h-3 w-3" />
          Filtro por rango de fechas <strong>reservado T-16.2</strong> · pendiente de soporte backend.
        </div>
      </WidgetCard>

      <WidgetCard
        title={`Histórico · ${total} ejecuciones`}
        description="Ordenado por created_at desc · 50/página"
        citations={["/api/automation/executions"]}
        testId="widget-history-list"
      >
        {q.isLoading && <LoadingState />}
        {q.error && <EmptyState message="No se pudo cargar el histórico." />}
        {q.data && items.length === 0 && (
          <EmptyState message="Sin resultados." testId="history-empty" />
        )}
        {items.length > 0 && (
          <ExecutionsTable
            items={items}
            total={total}
            page={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            onSelectRow={(e) => setSelected(e.id)}
            testIdPrefix="history"
            fetching={q.isFetching}
          />
        )}
      </WidgetCard>

      {selected && <ExecutionDetailPanel executionId={selected} onClose={() => setSelected(null)} />}

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["retention 90 días", "backend T-16 audit trail"]} />
      </footer>
    </section>
  );
}
